import { taggableRecordTypes, type TaggableRecordType } from '../taggedRecord';

/**
 * The Module list and the record.module() resolver (piece 8; decisions 0091,
 * 0139, 0148, 0505). Thirteen Modules, the complete list, in the
 * specification's order with Site inserted and Expenses appended. A Module is a
 * record type, and the navigation groups are a view of them. Employment gets no
 * member: an EmploymentRecord is gated by its Person's module.
 */
export const MODULES = ['Company', 'Person', 'Location', 'Site', 'Deal', 'Quote', 'SalesOrder', 'Invoice', 'PurchaseOrder', 'WorkOrder', 'Item', 'Accounting', 'Expenses'] as const;
export type Module = (typeof MODULES)[number];

export function isModule(value: string): value is Module {
  return (MODULES as readonly string[]).includes(value);
}

/** The eight classes; a FileType carries one or none, and a role's grid holds a set of them (decisions 0068, 0073, 0075). */
export const DOCUMENT_CLASSES = ['Public', 'Accounting', 'Purchasing', 'HR', 'Engineering', 'Legal', 'Commercial', 'General'] as const;
export type DocumentClass = (typeof DOCUMENT_CLASSES)[number];

export function isDocumentClass(value: string): value is DocumentClass {
  return (DOCUMENT_CLASSES as readonly string[]).includes(value);
}

/**
 * The third and last source of a file's class: one row per Module. Person is
 * HR because an employee file reached through the Person module is the case
 * that costs most to get wrong, and no Module defaults to Public.
 */
export const MODULE_DEFAULT_DOCUMENT_CLASS: Readonly<Record<Module, DocumentClass>> = {
  Company: 'General',
  Person: 'HR',
  Location: 'General',
  Site: 'General',
  Deal: 'Commercial',
  Quote: 'Commercial',
  SalesOrder: 'Commercial',
  Invoice: 'Accounting',
  PurchaseOrder: 'Purchasing',
  WorkOrder: 'General',
  Item: 'Engineering',
  Accounting: 'Accounting',
  Expenses: 'Accounting',
};

/**
 * The record.module() mapping, one row per record type, written as data so
 * tooling checks it. The domain runs wider than TaggableRecordType: the HR and
 * rate records keyed on an employment, and the six documents with a record
 * page of their own. A File is never its own member; canOpenFile step 2 reads
 * the owning record's.
 */
export const RECORD_MODULE: Readonly<Record<string, Module>> = {
  CompanyRecord: 'Company',
  PersonRecord: 'Person',
  LocationRecord: 'Location',
  SiteRecord: 'Site',
  Deal: 'Deal',
  Quote: 'Quote',
  SalesOrder: 'SalesOrder',
  Invoice: 'Invoice',
  PurchaseOrder: 'PurchaseOrder',
  WorkOrder: 'WorkOrder',
  Item: 'Item',
  AssetNode: 'Item',
  EmploymentRecord: 'Person',
  PTORequest: 'Person',
  Message: 'Accounting',
  Lead: 'Deal',
  Agreement: 'Deal',
  Bill: 'Accounting',
  Payment: 'Accounting',
  RFQ: 'PurchaseOrder',
  BOM: 'Deal',
  StockTransfer: 'Item',
  StockCount: 'Item',
  TaxRegistration: 'Accounting',
  PTOEnrollment: 'Person',
  PTOLedgerEntry: 'Person',
  SeparationRecord: 'Person',
  DisciplinaryRecord: 'Person',
  // Decision 10651: a claim is read on the module its employment is, and its routes ask hr.hire.
  UnemploymentClaim: 'Person',
  BreakAttestation: 'Person',
  OvertimeAuthorization: 'Person',
  CompensationRecord: 'Person',
  PersonCommissionOverride: 'Person',
  // Decision 1468: a complaint is an HR record, so it reads on the Person module's step and canViewHrRecord answers the record.
  ComplaintRecord: 'Person',
  CommissionRate: 'Accounting',
  TimeEntry: 'Person',
  CreditNote: 'Accounting',
  ChangeOrder: 'Accounting',
  VendorCredit: 'Accounting',
  Shipment: 'Accounting',
  StockRevaluation: 'Accounting',
  ExpenseReport: 'Expenses',
  // Decision 10042: an expense line is read on the module its report is, so a message filed against one reaches nobody Expenses keeps out.
  ExpenseLine: 'Expenses',
  // Decision 0761: a note on a Manual Journal is read and written on the Accounting module its nav row reads.
  JournalEntry: 'Accounting',
};

/** The HR records the record-level answer for which is canViewHrRecord, not the module step. */
export const HR_RECORD_TYPES = [
  'PTORequest', 'PTOEnrollment', 'PTOLedgerEntry', 'SeparationRecord', 'DisciplinaryRecord', 'BreakAttestation', 'OvertimeAuthorization',
  'CompensationRecord', 'PersonCommissionOverride',
  // Decision 1468: the complaint, answered with the management chain outside its audience.
  'ComplaintRecord',
] as const;

export class ModuleMapError extends Error {}

/**
 * record.module(): total over every record type it is called on, and never
 * undefined. An unmapped record type is the boot failure, not a falsy read,
 * because modulePermissions[undefined]?.read would deny everything silently.
 * A File answers with the record it is tagged to.
 */
export function recordModule(recordType: string, taggedTo?: string): Module {
  if (recordType === 'File') {
    if (!taggedTo) throw new ModuleMapError('a File is never its own member: its module is the record it is tagged to');
    return recordModule(taggedTo);
  }
  const module = RECORD_MODULE[recordType];
  if (!module) throw new ModuleMapError(`record type ${recordType} has no record.module() row; add its row before it reaches a gate`);
  return module;
}

/**
 * The boot check: every TaggableRecordType member is answered, so the
 * enumeration and the table move together.
 * 🔨 `File` is a member of the enumeration as a `Note` target (decision 0605)
 * and is never its own Module member: its module is the record it is tagged to,
 * so it is answered by `recordModule`'s own branch rather than by a row here.
 * Piece 8's mapping table carries it that way, and `tools/check-modules.py`
 * exempts it for the same reason.
 */
export function assertModuleMapTotal(types: readonly string[] = taggableRecordTypes): void {
  const missing = types.filter((t) => t !== 'File' && !(t in RECORD_MODULE));
  if (missing.length > 0) throw new ModuleMapError(`record.module() has no row for ${missing.join(', ')}`);
}

/** 🔨 `File` is never asked here: it answers through the record it is tagged to (decision 0605). */
export function moduleOfTaggable(recordType: Exclude<TaggableRecordType, 'File'>): Module {
  return recordModule(recordType);
}
